import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useAdmin } from "../contexts/AdminContext";
import { StatusBadge } from "../components/StatusBadge";
import { CopyPrincipal } from "../components/CopyPrincipal";
import { ErrorBanner } from "../components/ErrorBanner";
import { formatKes, formatCycles, daysFromHealth, formatDate } from "../lib/utils";
import type { ShopStats, ShopStatus, ShopRecord, MpesaShortcode, ShopSubscription, EtimsApiKeyUpdate } from "../lib/types";

const SUB_PAGE_SIZE = 50n;

type ShortcodeKind = "none" | "till" | "paybill";

export function ShopDetailPage() {
  const { num } = useParams<{ num: string }>();
  const { actor } = useAdmin();
  const shopNumber = Number(num);

  const [stats, setStats] = useState<ShopStats | null>(null);
  const [shopStatus, setShopStatus] = useState<ShopStatus | null>(null);
  const [shopRecord, setShopRecord] = useState<ShopRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Subscription list state
  const [subsOpen, setSubsOpen] = useState(false);
  const [subs, setSubs] = useState<ShopSubscription[]>([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState("");
  const [subsNextCursor, setSubsNextCursor] = useState<string | null>(null);
  const [subsCursorStack, setSubsCursorStack] = useState<(string | null)[]>([null]);

  const fetchSubs = useCallback(async (cursor: string | null) => {
    if (!actor || !shopNumber) return;
    setSubsLoading(true);
    setSubsError("");
    try {
      const res: { ok?: { items: ShopSubscription[]; nextCursor: [] | [string] }; err?: unknown } =
        await actor.listShopSubscriptions(BigInt(shopNumber), cursor ? [cursor] : [], SUB_PAGE_SIZE);
      if ("ok" in res) {
        setSubs(res.ok!.items);
        setSubsNextCursor(res.ok!.nextCursor[0] ?? null);
      } else {
        setSubsError("Failed to load subscriptions");
      }
    } catch (e) {
      setSubsError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setSubsLoading(false);
    }
  }, [actor, shopNumber]);

  function toggleSubs() {
    if (!subsOpen && subs.length === 0) fetchSubs(null);
    setSubsOpen((v) => !v);
  }

  function subsNextPage() {
    if (!subsNextCursor) return;
    setSubsCursorStack((s) => [...s, subsNextCursor]);
    fetchSubs(subsNextCursor);
  }

  function subsPrevPage() {
    if (subsCursorStack.length <= 1) return;
    const newStack = subsCursorStack.slice(0, -1);
    setSubsCursorStack(newStack);
    fetchSubs(newStack[newStack.length - 1]);
  }

  // Shortcode form state
  const [shortcodeKind, setShortcodeKind] = useState<ShortcodeKind>("none");
  const [tillNumber, setTillNumber] = useState("");
  const [paybillNumber, setPaybillNumber] = useState("");
  const [paybillAccount, setPaybillAccount] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  // eTIMS (DigiTax) form state
  const [etimsEnabled, setEtimsEnabled] = useState(false);
  const [etimsApiKeyInput, setEtimsApiKeyInput] = useState("");
  const [etimsApiKeyChanged, setEtimsApiKeyChanged] = useState(false);
  const [etimsApiKeyMasked, setEtimsApiKeyMasked] = useState<string | null>(null);
  const [etimsApiKeyVisible, setEtimsApiKeyVisible] = useState(false);
  const [etimsBusinessPin, setEtimsBusinessPin] = useState("");
  const [etimsSaving, setEtimsSaving] = useState(false);
  const [etimsSaveError, setEtimsSaveError] = useState("");
  const [etimsSaveSuccess, setEtimsSaveSuccess] = useState(false);
  const [etimsTesting, setEtimsTesting] = useState(false);
  const [etimsTestResult, setEtimsTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    if (!actor || !shopNumber) return;
    (async () => {
      try {
        const detailRes = await actor.getShopDetail(BigInt(shopNumber)) as {
          ok?: { shop: ShopRecord; stats: ShopStats; status: [] | [ShopStatus] };
          err?: unknown;
        };
        if (!("ok" in detailRes)) { setError("Failed to load shop"); return; }
        setStats(detailRes.ok!.stats);
        if (detailRes.ok!.status[0]) setShopStatus(detailRes.ok!.status[0]);
        setShopRecord(detailRes.ok!.shop);
        initShortcodeForm(detailRes.ok!.shop.mpesaShortcode[0] ?? null);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load shop");
      } finally {
        setLoading(false);
      }
      try {
        const etimsRes = await actor.getShopEtimsConfig(BigInt(shopNumber)) as {
          ok?: { enabled: boolean; businessPin: [] | [string]; apiKeyMasked: [] | [string] };
          err?: unknown;
        };
        if ("ok" in etimsRes) {
          setEtimsEnabled(etimsRes.ok!.enabled);
          setEtimsBusinessPin(etimsRes.ok!.businessPin[0] ?? "");
          setEtimsApiKeyMasked(etimsRes.ok!.apiKeyMasked[0] ?? null);
        }
      } catch {
        // Non-fatal: eTIMS section just shows as unconfigured if this fails.
      }
    })();
  }, [actor, shopNumber]);

  async function saveEtimsConfig() {
    if (!actor) return;
    setEtimsSaving(true);
    setEtimsSaveError("");
    setEtimsSaveSuccess(false);
    try {
      let apiKeyUpdate: EtimsApiKeyUpdate = { Unchanged: null };
      if (etimsApiKeyChanged) {
        apiKeyUpdate = etimsApiKeyInput.trim()
          ? { Set: etimsApiKeyInput.trim() }
          : { Clear: null };
      }
      if (etimsEnabled && !etimsApiKeyChanged && etimsApiKeyMasked === null) {
        setEtimsSaveError("An API key is required to enable eTIMS e-invoicing");
        return;
      }
      const res = await actor.setShopEtimsConfig(BigInt(shopNumber), {
        enabled: etimsEnabled,
        apiKeyUpdate,
        businessPin: etimsBusinessPin.trim() ? [etimsBusinessPin.trim()] : [],
      }) as { ok?: null; err?: unknown };
      if ("err" in res) { setEtimsSaveError("Failed to save: " + JSON.stringify(res.err)); return; }
      setEtimsSaveSuccess(true);
      setEtimsApiKeyChanged(false);
      setEtimsApiKeyInput("");
      const refreshed = await actor.getShopEtimsConfig(BigInt(shopNumber)) as {
        ok?: { enabled: boolean; businessPin: [] | [string]; apiKeyMasked: [] | [string] };
        err?: unknown;
      };
      if ("ok" in refreshed) setEtimsApiKeyMasked(refreshed.ok!.apiKeyMasked[0] ?? null);
      setTimeout(() => setEtimsSaveSuccess(false), 3000);
    } catch (e) {
      setEtimsSaveError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setEtimsSaving(false);
    }
  }

  async function testEtimsConnection() {
    if (!actor) return;
    setEtimsTesting(true);
    setEtimsTestResult(null);
    try {
      const res = await actor.testShopEtimsConnection(BigInt(shopNumber)) as { ok?: string; err?: unknown };
      if ("ok" in res) {
        setEtimsTestResult({ ok: true, message: res.ok! });
      } else {
        setEtimsTestResult({ ok: false, message: JSON.stringify(res.err) });
      }
    } catch (e) {
      setEtimsTestResult({ ok: false, message: e instanceof Error ? e.message : "Test failed" });
    } finally {
      setEtimsTesting(false);
    }
  }

  function initShortcodeForm(sc: MpesaShortcode | null) {
    if (!sc) {
      setShortcodeKind("none");
    } else if ("Till" in sc) {
      setShortcodeKind("till");
      setTillNumber(sc.Till);
    } else {
      setShortcodeKind("paybill");
      setPaybillNumber(sc.Paybill.number);
      setPaybillAccount(sc.Paybill.account);
    }
  }

  async function saveShortcode() {
    if (!actor) return;
    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);
    try {
      let shortcode: [] | [MpesaShortcode];
      if (shortcodeKind === "none") {
        shortcode = [];
      } else if (shortcodeKind === "till") {
        if (!tillNumber.trim()) { setSaveError("Till number is required"); setSaving(false); return; }
        shortcode = [{ Till: tillNumber.trim() }];
      } else {
        if (!paybillNumber.trim()) { setSaveError("Paybill number is required"); setSaving(false); return; }
        if (!paybillAccount.trim()) { setSaveError("Account number is required"); setSaving(false); return; }
        shortcode = [{ Paybill: { number: paybillNumber.trim(), account: paybillAccount.trim() } }];
      }
      const res = await actor.setShopMpesaShortcode(BigInt(shopNumber), shortcode) as { ok?: null; err?: unknown };
      if ("err" in res) { setSaveError("Failed to save: " + JSON.stringify(res.err)); return; }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p style={{ color: "var(--color-muted)" }}>Loading…</p>;

  const days = shopStatus ? daysFromHealth(shopStatus.health) : null;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Link to="/shops" style={{ color: "var(--color-muted)", fontSize: 13 }}>← Shops</Link>
      </div>
      <h1 style={{ fontSize: 24, marginBottom: 8 }}>
        Shop #{shopNumber}
        {stats && <span style={{ color: "var(--color-muted)", fontSize: 16, fontWeight: 400, marginLeft: 12 }}>{stats.id.toText().slice(0, 8)}…</span>}
      </h1>
      {stats && (
        <div style={{ marginBottom: 24 }}>
          <CopyPrincipal principal={stats.id.toText()} full />
        </div>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
        {shopRecord !== null && (
          <div className="card">
            <h3 style={cardHeadStyle}>Shop Details</h3>
            <DetailRow label="Name" value={shopRecord.name} />
            {shopRecord.contacts[0] && (
              <DetailRow label="Contacts" value={shopRecord.contacts[0]} />
            )}
            {shopRecord.address[0] && (
              <DetailRow label="Address" value={shopRecord.address[0]} />
            )}
            <DetailRow label="Account" value={shopRecord.accountNumber} mono />
          </div>
        )}

        {stats && (
          <div className="card">
            <h3 style={cardHeadStyle}>Shop Stats</h3>
            <StatRow label="Total Sales" value={String(Number(stats.totalSales))} />
            <StatRow label="Revenue" value={formatKes(stats.totalSalesKes)} accent />
            <StatRow label="Avg / Sale" value={formatKes(stats.salesAvg)} />
            <StatRow label="Avg / Day" value={formatKes(stats.dailyAvg)} />
            <StatRow label="Products" value={String(Number(stats.totalProducts))} />
            <StatRow label="Staff" value={String(Number(stats.totalStoreUsers))} />
          </div>
        )}

        {shopStatus && days !== null && (
          <div className="card">
            <h3 style={cardHeadStyle}>Canister Health</h3>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 22, fontFamily: "'DM Mono', monospace", color: "var(--color-text)" }}>
                {formatCycles(shopStatus.cycles)}
              </span>
              <StatusBadge days={days} health={shopStatus.health} />
            </div>
            <StatRow label="Cycles Balance" value={formatCycles(shopStatus.cycles)} />
            <StatRow label="Burn / Day" value={formatCycles(shopStatus.idleCyclesBurnedPerDay)} />
            <StatRow label="Days Left" value={isFinite(days) ? String(days) : "∞"} />
            <StatRow label="Memory" value={`${(Number(shopStatus.memorySize) / 1024 / 1024).toFixed(1)} MB`} />
          </div>
        )}

        {shopRecord !== null && (
          <div className="card">
            <h3 style={cardHeadStyle}>M-Pesa Shortcode</h3>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Type</label>
              <select
                value={shortcodeKind}
                onChange={e => setShortcodeKind(e.target.value as ShortcodeKind)}
                style={inputStyle}
              >
                <option value="none">None</option>
                <option value="till">Till</option>
                <option value="paybill">Paybill</option>
              </select>
            </div>

            {shortcodeKind === "till" && (
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Till Number</label>
                <input
                  type="text"
                  value={tillNumber}
                  onChange={e => setTillNumber(e.target.value)}
                  placeholder="e.g. 123456"
                  style={inputStyle}
                />
              </div>
            )}

            {shortcodeKind === "paybill" && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Paybill Number</label>
                  <input
                    type="text"
                    value={paybillNumber}
                    onChange={e => setPaybillNumber(e.target.value)}
                    placeholder="e.g. 400200"
                    style={inputStyle}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Account Number</label>
                  <input
                    type="text"
                    value={paybillAccount}
                    onChange={e => setPaybillAccount(e.target.value)}
                    placeholder="e.g. 0712345678"
                    style={inputStyle}
                  />
                </div>
              </>
            )}

            {saveError && <p style={{ color: "var(--color-error, #f87171)", fontSize: 13, marginBottom: 8 }}>{saveError}</p>}
            {saveSuccess && <p style={{ color: "var(--color-accent)", fontSize: 13, marginBottom: 8 }}>Saved.</p>}

            <button
              onClick={saveShortcode}
              disabled={saving}
              style={{ marginTop: 4, padding: "8px 16px", background: "var(--color-accent)", color: "#0A0F0D",
                border: "none", borderRadius: 4, cursor: saving ? "not-allowed" : "pointer",
                fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
                opacity: saving ? 0.6 : 1 }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}

        {shopRecord !== null && (
          <div className="card">
            <h3 style={cardHeadStyle}>eTIMS (DigiTax)</h3>

            <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                id="etims-enabled"
                checked={etimsEnabled}
                onChange={e => setEtimsEnabled(e.target.checked)}
                style={{ width: 18, height: 18 }}
              />
              <label htmlFor="etims-enabled" style={{ fontSize: 13, color: "var(--color-text)", cursor: "pointer" }}>
                Enable eTIMS e-invoicing
              </label>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>API Key</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type={etimsApiKeyVisible ? "text" : "password"}
                  value={etimsApiKeyInput}
                  onChange={e => { setEtimsApiKeyInput(e.target.value); setEtimsApiKeyChanged(true); }}
                  placeholder={etimsApiKeyMasked ?? "Not set"}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => setEtimsApiKeyVisible(v => !v)}
                  style={{ background: "none", border: "1px solid var(--color-border)", borderRadius: 4,
                    padding: "0 10px", cursor: "pointer", color: "var(--color-muted)" }}
                >
                  {etimsApiKeyVisible ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Business PIN</label>
              <input
                type="text"
                value={etimsBusinessPin}
                onChange={e => setEtimsBusinessPin(e.target.value)}
                placeholder="e.g. P123456789Y"
                style={inputStyle}
              />
            </div>

            {etimsSaveError && <p style={{ color: "var(--color-error, #f87171)", fontSize: 13, marginBottom: 8 }}>{etimsSaveError}</p>}
            {etimsSaveSuccess && <p style={{ color: "var(--color-accent)", fontSize: 13, marginBottom: 8 }}>Saved.</p>}

            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={saveEtimsConfig}
                disabled={etimsSaving}
                style={{ padding: "8px 16px", background: "var(--color-accent)", color: "#0A0F0D",
                  border: "none", borderRadius: 4, cursor: etimsSaving ? "not-allowed" : "pointer",
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
                  opacity: etimsSaving ? 0.6 : 1 }}
              >
                {etimsSaving ? "Saving…" : "Save"}
              </button>
              <button
                onClick={testEtimsConnection}
                disabled={etimsTesting || etimsApiKeyMasked === null}
                style={{ padding: "8px 16px", background: "none", color: "var(--color-text)",
                  border: "1px solid var(--color-border)", borderRadius: 4,
                  cursor: etimsTesting || etimsApiKeyMasked === null ? "not-allowed" : "pointer",
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
                  opacity: etimsTesting || etimsApiKeyMasked === null ? 0.6 : 1 }}
              >
                {etimsTesting ? "Testing…" : "Test Connection"}
              </button>
            </div>

            {etimsTestResult && (
              <p style={{ marginTop: 8, fontSize: 13,
                color: etimsTestResult.ok ? "var(--color-accent)" : "var(--color-error, #f87171)" }}>
                {etimsTestResult.message}
              </p>
            )}
          </div>
        )}
      </div>
      <div style={{ marginTop: 32 }}>
        <button
          onClick={toggleSubs}
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-muted)",
            fontSize: 14, fontWeight: 600, padding: 0, display: "flex", alignItems: "center", gap: 8 }}
        >
          <span style={{ fontSize: 12 }}>{subsOpen ? "▾" : "▸"}</span>
          Subscriptions
        </button>

        {subsOpen && (
          <div style={{ marginTop: 12 }}>
            {subsError && <ErrorBanner message={subsError} onDismiss={() => setSubsError("")} />}
            {subsLoading ? (
              <p style={{ color: "var(--color-muted)", fontSize: 13 }}>Loading…</p>
            ) : subs.length === 0 ? (
              <p style={{ color: "var(--color-muted)", fontSize: 13 }}>No subscriptions.</p>
            ) : (
              <div className="card" style={{ padding: 0, overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Started</th>
                      <th>Expires</th>
                      <th>Duration</th>
                      <th>Via</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subs.map((sub) => {
                      const via =
                        sub.subscriptionCode[0] != null ? "free-code"
                        : sub.mpesaRef[0] != null ? "mpesa"
                        : "free-trial";
                      const viaStyles: Record<string, { background: string; color: string; border: string; label: string }> = {
                        "mpesa":      { background: "rgba(0,232,122,0.12)",  color: "var(--color-accent)", border: "1px solid rgba(0,232,122,0.3)",  label: "M-Pesa" },
                        "free-code":  { background: "rgba(59,130,246,0.12)", color: "#60a5fa",             border: "1px solid rgba(59,130,246,0.3)", label: "Free Code" },
                        "free-trial": { background: "rgba(168,85,247,0.12)", color: "#c084fc",             border: "1px solid rgba(168,85,247,0.3)", label: "Free Trial" },
                      };
                      const detail = sub.subscriptionCode[0] ?? sub.mpesaRef[0];
                      return (
                        <tr key={String(sub.id)}>
                          <td className="mono" style={{ color: "var(--color-muted)" }}>{String(sub.id)}</td>
                          <td style={{ fontSize: 13, whiteSpace: "nowrap", color: "var(--color-muted)" }}>{formatDate(sub.started)}</td>
                          <td style={{ fontSize: 13, whiteSpace: "nowrap", color: "var(--color-muted)" }}>{formatDate(sub.expires)}</td>
                          <td className="mono" style={{ fontSize: 13, color: "var(--color-muted)" }}>{String(sub.durationDays)}d</td>
                          <td>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 4, ...viaStyles[via] }}>
                              {viaStyles[via].label}
                            </span>
                          </td>
                          <td>
                            {detail
                              ? <span className="mono" style={{ fontSize: 12, color: "var(--color-muted)" }}>{detail}</span>
                              : <span style={{ color: "var(--color-muted)", fontSize: 12 }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {(subsCursorStack.length > 1 || subsNextCursor) && (
              <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                <button onClick={subsPrevPage} disabled={subsCursorStack.length <= 1} style={pageBtnStyle(subsCursorStack.length <= 1)}>← Prev</button>
                <button onClick={subsNextPage} disabled={!subsNextCursor} style={pageBtnStyle(!subsNextCursor)}>Next →</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function pageBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "8px 16px", borderRadius: 4, border: "1px solid var(--color-border)",
    background: "var(--color-surface)", color: disabled ? "var(--color-muted)" : "var(--color-text)",
    cursor: disabled ? "not-allowed" : "pointer", fontSize: 13, opacity: disabled ? 0.5 : 1,
  };
}

const cardHeadStyle: React.CSSProperties = {
  marginBottom: 16, fontSize: 14, color: "var(--color-muted)",
  fontFamily: "'DM Sans', sans-serif", textTransform: "uppercase", letterSpacing: "0.05em",
};

const labelStyle: React.CSSProperties = {
  display: "block", fontSize: 12, color: "var(--color-muted)",
  marginBottom: 4, fontFamily: "'DM Sans', sans-serif",
};

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "7px 10px", background: "var(--color-surface)",
  border: "1px solid var(--color-border)", borderRadius: 4, color: "var(--color-text)",
  fontFamily: "'DM Mono', monospace", fontSize: 13, boxSizing: "border-box",
};

function StatRow({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "8px 0", borderBottom: "1px solid var(--color-border)" }}>
      <span style={{ fontSize: 13, color: "var(--color-muted)" }}>{label}</span>
      <span style={{ fontSize: 14, fontFamily: "'DM Mono', monospace",
        color: accent ? "var(--color-accent)" : "var(--color-text)", fontWeight: 500 }}>
        {value}
      </span>
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ padding: "8px 0", borderBottom: "1px solid var(--color-border)" }}>
      <div style={{ fontSize: 12, color: "var(--color-muted)", marginBottom: 3 }}>{label}</div>
      <div style={{
        fontSize: 14,
        color: "var(--color-text)",
        fontFamily: mono ? "'DM Mono', monospace" : "'DM Sans', sans-serif",
        lineHeight: 1.45,
        overflowWrap: "anywhere",
      }}>
        {value}
      </div>
    </div>
  );
}
